import { Button, Label } from '@lensora/ui'
import * as React from 'react'

import { SuggestionChips } from '../SuggestionChips'

export const INDUSTRIES = [
  'SaaS / Software', 'E-commerce', 'Marketing / Agency', 'Financial Services',
  'Healthcare', 'Real Estate', 'Education', 'Legal', 'Manufacturing',
  'Travel & Hospitality', 'Other',
]

interface ContextTurnProps {
  resolved: boolean
  summary?: string | undefined
  prefillIndustry?: string | undefined
  prefillNiche?: string | undefined
  saving: boolean
  error: string | null
  onSubmit: (industry: string, niche: string, acceptedSuggestion: boolean) => void
  // Lifted to OnboardingChat so the persistent composer bar can populate this
  // field directly, exactly as if the user had typed into the textarea.
  niche: string
  onNicheChange: (value: string) => void
}

export function ContextTurn({ resolved, summary, prefillIndustry, prefillNiche, saving, error, onSubmit, niche, onNicheChange }: ContextTurnProps) {
  const [industry, setIndustry] = React.useState(prefillIndustry ?? '')
  // Starts collapsed whenever there's a high-confidence prefill to confirm —
  // the box only needs to appear once the user says the guess is wrong,
  // rather than always being visible next to a yes/no choice that implies
  // there's nothing left to fill in.
  const [editing, setEditing] = React.useState(!prefillIndustry)

  if (resolved) {
    return <p className="text-sm text-text-muted">{summary}</p>
  }

  const accepted = !!prefillIndustry && industry === prefillIndustry && niche === (prefillNiche ?? '')
  const showEditor = editing || !accepted

  return (
    <div className="space-y-3">
      {showEditor && (
        <>
          <SuggestionChips options={INDUSTRIES} selected={(o) => o === industry} onToggle={setIndustry} disabled={saving} />
          <div className="space-y-1.5">
            <Label htmlFor="onboarding-niche">
              What do you do? <span className="font-normal text-text-muted">(optional)</span>
            </Label>
            <textarea
              id="onboarding-niche"
              value={niche}
              onChange={(e) => onNicheChange(e.target.value)}
              placeholder="e.g. I run a SaaS tool for small accountants to automate tax filing…"
              rows={2}
              className="w-full resize-none rounded-lg border border-border bg-surface px-3.5 py-2.5 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
            />
          </div>
        </>
      )}

      {error && <p className="text-sm text-feedback-error">{error}</p>}

      {showEditor ? (
        <Button size="sm" disabled={!industry || saving} onClick={() => onSubmit(industry, niche.trim(), accepted)}>
          {saving ? 'Saving…' : 'Continue →'}
        </Button>
      ) : (
        <div className="flex gap-2.5">
          <Button size="sm" disabled={saving} onClick={() => onSubmit(industry, niche.trim(), true)}>
            {saving ? 'Saving…' : 'Sounds right →'}
          </Button>
          <Button size="sm" variant="outline" disabled={saving} onClick={() => setEditing(true)}>
            Not quite
          </Button>
        </div>
      )}
    </div>
  )
}
